import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '@/lib/api/client';
import { apiFetch } from '@/lib/apiClient';
import type { WorkspacePlanTier } from '@/features/billing/hooks/useWorkspacePlanTier';

interface QuotaData {
  planTier: string;
  dailyUsed: number;
  dailyLimit: number;
  weeklyUsed: number;
  weeklyLimit: number;
  resetsAt: string;
  storageUsedBytes: number;
  storageLimitBytes: number;
}

interface Props {
  workspaceId: string;
  token: string;
  tier: WorkspacePlanTier | null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatTokens(tokens: number): string {
  if (tokens < 1000) return String(tokens);
  if (tokens < 1_000_000) return `${(tokens / 1000).toFixed(1)}K`;
  return `${(tokens / 1_000_000).toFixed(1)}M`;
}

function usagePercent(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

export function BillingSection({ workspaceId, token, tier }: Props) {
  const [quota, setQuota] = useState<QuotaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const loadQuota = useCallback(() => {
    setLoading(true);
    apiGet<QuotaData>(`/workspaces/${workspaceId}/quota`)
      .then(res => setQuota(res.data))
      .catch(() => setQuota(null))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  const handleManageSubscription = async () => {
    setOpeningPortal(true);
    setPortalError(null);
    try {
      const res = await apiFetch<{ url?: string }>('payment/portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ returnUrl: window.location.href }),
        authToken: token,
      });
      if (res?.url) {
        window.location.href = res.url;
        return;
      }
      setPortalError('Could not open the billing portal. Please try again.');
    } catch (err) {
      console.error('Failed to open billing portal', err);
      setPortalError('Could not open the billing portal. Please try again.');
    } finally {
      setOpeningPortal(false);
    }
  };

  if (loading) return <p className="ws-settings-muted">Loading billing info...</p>;
  if (!quota) {
    return (
      <div>
        <p className="ws-settings-muted">Unable to load billing information.</p>
        <button type="button" onClick={loadQuota} className="ws-settings-btn-secondary">Retry</button>
      </div>
    );
  }

  const tierLabel = tier === 'pro' ? 'Professional' : tier === 'team' ? 'Team' : 'Free';
  const isPaid = tier === 'pro' || tier === 'team';
  const dailyPct = usagePercent(quota.dailyUsed, quota.dailyLimit);
  const weeklyPct = usagePercent(quota.weeklyUsed, quota.weeklyLimit);
  const storagePct = usagePercent(quota.storageUsedBytes, quota.storageLimitBytes);

  return (
    <div>
      <div className="ws-settings-section-header">
        <h2>Billing</h2>
        {isPaid && (
          <button onClick={handleManageSubscription} disabled={openingPortal} className="ws-settings-btn-secondary">
            {openingPortal ? 'Opening...' : 'Manage billing'}
          </button>
        )}
      </div>

      {portalError && <p className="ws-settings-error">{portalError}</p>}

      <div className="ws-settings-card">
        <div className="ws-settings-card-row">
          <span className="ws-settings-card-label">Plan</span>
          <span className="ws-settings-card-value">{tierLabel}</span>
        </div>
        <div className="ws-settings-card-row">
          <span className="ws-settings-card-label">Resets at</span>
          <span className="ws-settings-card-value">{new Date(quota.resetsAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</span>
        </div>
      </div>

      <h3 className="ws-settings-h3">Usage</h3>

      <div className="ws-settings-usage-grid">
        <UsageBar label="Daily tokens" used={quota.dailyUsed} limit={quota.dailyLimit} pct={dailyPct} format={formatTokens} />
        <UsageBar label="Weekly tokens" used={quota.weeklyUsed} limit={quota.weeklyLimit} pct={weeklyPct} format={formatTokens} />
        <UsageBar label="Storage" used={quota.storageUsedBytes} limit={quota.storageLimitBytes} pct={storagePct} format={formatBytes} />
      </div>
    </div>
  );
}

function UsageBar({ label, used, limit, pct, format }: { label: string; used: number; limit: number; pct: number; format: (n: number) => string }) {
  return (
    <div className="ws-settings-usage-item">
      <div className="ws-settings-usage-header">
        <span>{label}</span>
        <span className="ws-settings-muted">{format(used)} / {format(limit)}</span>
      </div>
      <div className="ws-settings-progress">
        <div className="ws-settings-progress-fill" style={{ width: `${pct}%`, background: pct > 90 ? 'var(--color-danger, #EA4335)' : 'var(--color-primary, #0EA5E9)' }} />
      </div>
    </div>
  );
}
